import React, { useCallback } from 'react';
import { Card, Col } from 'react-bootstrap';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { ReceivedShiftChangeRequestTableItem } from './mappings';
import { TableContentManual } from '../../../manager/components/TableContentManual';
import { CellProps, Column } from 'react-table';
import FormattedDateTime from '../../../../../components/datetime/FormattedDateTime';
import { EmployeeWithAvatar } from '../../../components/Employee';
import { ShiftDisplay } from '../SchedulerShiftsCalendar/ShiftDisplay';
import {
  getColleagueBadgeColor,
  getDetailedStatusBadgeColor,
} from '../SchedulerShiftsCalendar/shiftChangeRequestUtils';
import { ShiftChangeRequestColleagueApprovalStatus } from '../../../__generated__/graphql';
import {
  ApproveRejectActionsCell,
  ApproveRejectModal,
} from './ApproveRejectActionsCell';
import { useRespondToShiftChangeRequest } from '../SchedulerShiftsCalendar/useShiftChangeRequestActions';
import { useShiftChangeRequestSidebar } from '../SchedulerShiftsCalendar/useShiftChangeRequestSidebar';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../components/ModalSidebar';
import { ShiftSidePanel } from '../SchedulerShiftsCalendar/ShiftSidePanel';
import { EmptyStateCardBody } from '../../../components/EmptyStateCard';
import EmptyStateImg from '../../../../../vendor/img/empty-state.png';

const PAGE_SIZE = 50;

const i18Path = 'schedulerShifts.receivedChangeRequests';
const i18PathDetailedStatus =
  'schedulerShifts.shiftChangeRequest.detailedStatus';

type Props = {
  requests: ReceivedShiftChangeRequestTableItem[];
  loading: boolean;
};

export const ReceivedShiftChangeRequestsTable: React.FC<Props> = ({
  requests,
  loading,
}) => {
  const tDetailedStatus = useNamespacedTranslation(i18PathDetailedStatus);
  const t = useNamespacedTranslation(i18Path);
  const { selectedShift, handleShiftClick, handleCloseSidebar } =
    useShiftChangeRequestSidebar<ReceivedShiftChangeRequestTableItem>({
      requests,
      loading,
    });
  const {
    requestToRespond,
    isResponding,
    handleAcceptClick,
    handleDeclineClick,
    handleRespondConfirm,
    handleRespondClose,
  } = useRespondToShiftChangeRequest();

  const actionsCell = useCallback(
    (props: CellProps<ReceivedShiftChangeRequestTableItem>) => {
      const request = props.row.original;
      const canRespond =
        request.colleagueApprovalStatus ===
        ShiftChangeRequestColleagueApprovalStatus.Pending;

      if (canRespond) {
        return (
          <ApproveRejectActionsCell
            requestId={request.id}
            requestToRespond={requestToRespond}
            isResponding={isResponding}
            onAcceptClick={handleAcceptClick}
            onDeclineClick={handleDeclineClick}
          />
        );
      }

      if (
        request.colleagueApprovalStatus ===
          ShiftChangeRequestColleagueApprovalStatus.Accepted &&
        request.statusDetailed
      ) {
        return (
          <Col>
            <span
              className={`badge ${getDetailedStatusBadgeColor(
                request.statusDetailed,
              )}`}>
              {tDetailedStatus(`${request.statusDetailed}`)}
            </span>
          </Col>
        );
      }

      return <Col></Col>;
    },
    [
      tDetailedStatus,
      requestToRespond,
      isResponding,
      handleAcceptClick,
      handleDeclineClick,
    ],
  );

  const columns: Column<ReceivedShiftChangeRequestTableItem>[] = [
    {
      Header: t('table.header.shift'),
      accessor: 'shiftTitle',
      Cell: (props: CellProps<ReceivedShiftChangeRequestTableItem>) => {
        const request = props.row.original;
        const shiftContent = (
          <ShiftDisplay
            title={request.shiftTitle}
            startDate={request.shiftStartDate}
            endDate={request.shiftEndDate}
          />
        );

        if (!request.shiftId) {
          return <Col>{shiftContent}</Col>;
        }

        return (
          <Col>
            <span
              className="link-primary link-underline-opacity-0 link-underline-opacity-100-hover"
              onClick={() => handleShiftClick(request)}
              role="button"
              tabIndex={0}>
              {shiftContent}
            </span>
          </Col>
        );
      },
    },
    {
      Header: t('table.header.requestedBy'),
      accessor: 'requestedByEmployee',
      Cell: (props: CellProps<ReceivedShiftChangeRequestTableItem>) => {
        const employee = props.row.original.requestedByEmployee;
        if (!employee) {
          return <Col>-</Col>;
        }
        return (
          <Col>
            <EmployeeWithAvatar
              employee={{
                name: employee.name,
                avatarUrl: null,
              }}
            />
          </Col>
        );
      },
    },
    {
      Header: t('table.header.status'),
      accessor: 'colleagueApprovalStatus',
      Cell: (props: CellProps<ReceivedShiftChangeRequestTableItem>) => {
        const status = props.row.original.colleagueApprovalStatus;
        if (!status) {
          return <Col>-</Col>;
        }
        return (
          <Col>
            <span className={`badge ${getColleagueBadgeColor(status)}`}>
              {t(`colleagueApprovalStatus.${status}`)}
            </span>
          </Col>
        );
      },
    },
    {
      Header: t('table.header.requestedAt'),
      accessor: 'requestedAt',
      Cell: (props: CellProps<ReceivedShiftChangeRequestTableItem>) => (
        <Col>
          <FormattedDateTime
            dateTime={props.row.original.requestedAt}
            format="long"
          />
        </Col>
      ),
    },
    {
      Header: t('table.header.actions'),
      accessor: 'actions',
      Cell: actionsCell,
    },
  ];

  const renderTable = (requests: ReceivedShiftChangeRequestTableItem[]) => {
    if (!requests.length) {
      return <EmptyStateCardBody title={t('emptyState')} img={EmptyStateImg} />;
    }
    return (
      <TableContentManual
        data={requests}
        columns={columns}
        onRowClick={() => {}}
        pageSize={PAGE_SIZE}
        itemsCount={requests.length}
        initialSortBy={[]}
        onSortByChanged={() => {}}
        initialPageIndex={0}
        onPageIndexChanged={() => {}}
      />
    );
  };

  return (
    <>
      <Card>
        {requests !== undefined && !loading ? (
          renderTable(requests)
        ) : (
          <SkeletonTable className="card-table" columns={5} rows={8} />
        )}
        <ApproveRejectModal
          requestToRespond={requestToRespond}
          isResponding={isResponding}
          onConfirm={handleRespondConfirm}
          onClose={handleRespondClose}
        />
      </Card>
      {selectedShift && selectedShift.shiftId && (
        <ModalSidebar
          show={!!selectedShift}
          onClose={handleCloseSidebar}
          header={selectedShift.shiftTitle}
          content={
            <ModalSidebarContent
              body={<ShiftSidePanel shiftId={selectedShift.shiftId} />}
            />
          }
        />
      )}
    </>
  );
};
